// Global Instructions Rule Applied!
import dayjs from 'dayjs'

/** Placeholder shown when a value is missing/empty. */
export const EMPTY = '—'

/** Display a value, or an em-dash when it's null/undefined/blank. */
export function text(value: unknown): string {
  if (value === null || value === undefined) return EMPTY
  const s = String(value).trim()
  return s.length ? s : EMPTY
}

/** Format a raw number with thousands separators (no currency symbol). */
export function num(value: unknown): string {
  if (value === null || value === undefined || value === '') return EMPTY
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return EMPTY
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n)
}

/** First non-empty preformatted (`*_fmt`) string among `keys` on a row, else null. */
export function pickFmt(row: Record<string, unknown> | null | undefined, keys: string[]): string | null {
  if (!row) return null
  for (const k of keys) {
    const v = row[k]
    if (v !== null && v !== undefined && String(v).trim() !== '') return String(v)
  }
  return null
}

/** Ensure a user-entered URL has a protocol so it opens as an external link.
 *  (Promoted to core/utils/contactHref.ts 2026-08-26; re-exported here so the
 *  mobile screens keep their import path.) */
export { externalHref } from '../../../utils/contactHref.ts'

/** Format a date / timestamp as e.g. "12 Jun 2026"; em-dash when missing/invalid. */
export function dateText(value: unknown): string {
  if (value === null || value === undefined || value === '') return EMPTY
  const d = dayjs(String(value))
  return d.isValid() ? d.format('D MMM YYYY') : EMPTY
}

/** Format a timestamp as e.g. "12 Jun 2026, 14:30"; em-dash when missing/invalid. */
export function dateTimeText(value: unknown): string {
  if (value === null || value === undefined || value === '') return EMPTY
  const d = dayjs(String(value))
  return d.isValid() ? d.format('D MMM YYYY, HH:mm') : EMPTY
}

/** Human-readable file size from a byte count (e.g. "1.2 MB"); null when not a size. */
export function bytesText(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n < 0) return null
  if (n < 1024) return `${Math.round(n)} B`
  let v = n
  for (const unit of ['KB', 'MB', 'GB', 'TB']) {
    v = v / 1024
    if (v < 1024 || unit === 'TB') return `${v >= 100 ? Math.round(v) : v.toFixed(1)} ${unit}`
  }
  return null
}

/**
 * Display name for a UserEntry (interaction owner/participants, task assignees). The
 * entries are contact-shaped (`contacts__*` + a server-computed `prog__display_name`).
 * Returns '' when nothing usable so callers can `.filter(Boolean)`.
 */
export function userEntryLabel(entry: unknown): string {
  if (!entry || typeof entry !== 'object') return ''
  const e = entry as Record<string, unknown>
  const display = String(e.prog__display_name || '').trim()
  if (display) return display
  const name = personName(e.contacts__first_name, e.contacts__last_name, e.contacts__preferred_name, e.contacts__email)
  return name === 'Unknown' ? '' : name
}

/** Build a person's display name from name parts (preferred ?? first, then last). */
export function personName(
  firstName?: unknown,
  lastName?: unknown,
  preferredName?: unknown,
  fallback?: unknown
): string {
  const first = String(preferredName || firstName || '').trim()
  const last = String(lastName || '').trim()
  const full = `${first} ${last}`.trim()
  if (full) return full
  const fb = String(fallback || '').trim()
  return fb || 'Unknown'
}
